import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import { Box, Flex, Input, InputGroup, InputLeftElement, InputRightElement, List, ListItem, useColorModeValue, Spinner, Text } from '@chakra-ui/react';
import countries from 'i18n-iso-countries';
import { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt } from "react-icons/fa";
import { useGetLocationsForShoppingQuery } from 'store/facebookServices/facebookServicesApi';

// Register the English locale for i18n-iso-countries
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));


const LocationSelectorForShopping = ({ onLocationsChange, preFilledIncludedLocations, onBlur, adsAccountData, updatedRegions }) => {

    const inputBackground = useColorModeValue("#f8f6fb", "#1e1530");
    const borderColor = useColorModeValue('gray.300', 'gray.500');
    const hoverColor = useColorModeValue('#f0f0f0', 'gray.700');

    const flattenLocations = (locationsObj) => {
        if (Object.entries(locationsObj)?.length === 0) {
            return [];
        }

        const flattenedLocations = Object.entries(locationsObj)
            .filter(([key]) => key !== "location_types") // Exclude 'location_types'
            .flatMap(([type, locations]) =>
                locations.map((location) => {
                    // console.log({ location });

                    if (type === "countries" && !location?.type) {
                        // location.type shows entry from adspresso db & type is for fb entry
                        const fullName = countries.getName(location, 'en');

                        return {
                            key: location,
                            country_code: location,
                            name: fullName || location, // Use the ISO code if the name isn't found
                            type: "country",
                            supports_region: true,
                            supports_city: true,
                        };
                    }

                    return { ...location };
                })
            )

        return flattenedLocations;
    };

    // Helper function to check if a location should be replaced
    const shouldRemoveLocation = (newLocation, existingLocation) => {
        //   console.log({ newLocation, existingLocation });

        const isSameCountry = newLocation.country_code === existingLocation.country_code;

        // Check for identical locations (no removal)
        const isSameLocation =
            newLocation.key === existingLocation.key &&
            newLocation.type === existingLocation.type;

        // Check if regions and cities are being replaced by countries and vice versa
        const isRegionReplacedByCountry =
            (newLocation.type === "country" && (existingLocation.type === "region")) &&
            isSameCountry;

        const isCountryReplacedByRegion =
            (newLocation.type === "region") &&
            existingLocation.type === "country" &&
            isSameCountry;

        // Final conditions to determine if the location should be removed
        return (
            isRegionReplacedByCountry ||
            isCountryReplacedByRegion ||
            isSameLocation
        );
    };


    // Remove locations of different specificity when a new one is selected
    const filterSelectedLocations = (newLocation, selectedLocations) => {

        // Handle exclusion logic directly for exclude type
        if (newLocation.inclusionType === "exclude") {
            // First, remove any location that conflicts with the new exclusion location
            const updatedLocations = selectedLocations.filter(
                (location) => !shouldRemoveLocation(newLocation, location)
            );

            // Add the exclusion location
            return [...updatedLocations, newLocation];
        }

        // Handle inclusion type by filtering out locations that should be replaced
        const updatedLocations = selectedLocations.filter(
            (location) => !shouldRemoveLocation(newLocation, location)
        );

        // Include the new location
        return [...updatedLocations, newLocation];
    };

    const [selectedLocations, setSelectedLocations] = useState([]);

    useEffect(() => {
        const fetchAndCombineLocations = async () => {
            const flattenedPreFilledIncludedLocations = preFilledIncludedLocations
                ? await flattenLocations(preFilledIncludedLocations)
                : [];

            // Combine the arrays
            setSelectedLocations([
                ...flattenedPreFilledIncludedLocations,
            ]);
        };

        fetchAndCombineLocations();
    }, [preFilledIncludedLocations]);


    const inputRef = useRef(null);
    const popupRef = useRef(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [locationOptions, setLocationOptions] = useState([]);
    const [showPopup, setShowPopup] = useState(false);

    const { data: locations, isLoading, isFetching, refetch } = useGetLocationsForShoppingQuery({ searchTerm: debouncedSearchTerm, adsAccountId: adsAccountData }, {});

    useEffect(() => {
        if (searchTerm?.length === 0) {
            setDebouncedSearchTerm('');
            setShowPopup(false);
            return;
        }

        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 800);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (searchTerm?.length > 0 && searchTerm === debouncedSearchTerm) {
            refetch();
        }
    }, [searchTerm, debouncedSearchTerm]);

    useEffect(() => {
        if (locations?.data?.length > 0) {

            // Filter locations based on updatedRegions
            const filteredLocations = locations?.data.filter((location) => {
                return !updatedRegions.some(
                    (region) =>
                        region.key === location.key &&
                        region.country === location.country_code
                );
            });
            setShowPopup(filteredLocations.length > 0);
            setLocationOptions(filteredLocations);
        } else {
            setShowPopup(false);
        }
    }, [locations, updatedRegions, isFetching]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target) &&
                popupRef.current &&
                !popupRef.current.contains(event.target)
            ) {
                setShowPopup(false);
                setLocationOptions([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleLocationSelect = (location) => {
        //  console.log({ location });

        const locationWithInclusionType = { ...location };

        setSelectedLocations(prevLocations => {
            const updatedLocations = filterSelectedLocations(locationWithInclusionType, prevLocations);
            const allLocations = [...updatedLocations, locationWithInclusionType];

            const finalLocations = allLocations.filter((value, index, self) =>
                index === self.findIndex((t) => (
                    t.key === value.key
                ))
            );

            // Call the parent function to notify about the updated selected locations
            setTimeout(() => onLocationsChange(finalLocations), 0);

            return finalLocations;
        });

        setSearchTerm('');
        setDebouncedSearchTerm('');
        setShowPopup(false);
        setLocationOptions([]);
    };

    const removeLocation = (id) => {
        setSelectedLocations((prevLocations) => {
            const updatedLocations = prevLocations.filter((location) => location.key !== id);
            setTimeout(() => onLocationsChange(updatedLocations), 0);
            return updatedLocations;
        });
    };

    useEffect(() => {
        if (selectedLocations?.length) {
            onLocationsChange(selectedLocations)
        }
    }, [selectedLocations])

    return (
        <Box>
            <Box mt="10px" mb={'5px'} maxHeight="200px" overflowY="auto">
                {selectedLocations?.map((location, index) => (
                    <Flex key={`${location.key}-${index}`} bg={inputBackground} align={'center'} justify={'space-between'} width={'full'} py={'5px'} px={'10px'} borderRadius={'md'} mb={'5px'}>
                        <Flex flex={1} pr={'10px'} align={'center'}>
                            <FaMapMarkerAlt color={'#00A500'} size="16px" />
                            <Flex justify={'space-between'} flex={1} align={'center'}>
                                <Text ml={'5px'} fontSize={'14px'} fontWeight={'bold'}>{location.name} </Text>
                            </Flex>
                        </Flex>
                        <CloseIcon _hover={{ cursor: 'pointer', color: 'red.400' }} boxSize={3} color='gray.500' onClick={() => removeLocation(location?.key)} />
                    </Flex>
                ))}
            </Box>

            {/* Dropdown for inclusion/exclusion */}
            <Flex>
                <Box position="relative" flex={1}>
                    <InputGroup alignItems={'center'}>
                        <InputLeftElement height={'32px'}>
                            <SearchIcon boxSize={4} color='gray.500' />
                        </InputLeftElement>
                        <Input
                            id='geo_locations'
                            name='geo_locations'
                            ref={inputRef}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); }}
                            placeholder="Search locations"
                            autoComplete="off"
                            size={'sm'}
                            borderRadius={'md'}
                            borderLeftRadius={0}
                            onBlur={onBlur}
                            onFocus={() => {
                                setLocationOptions([]);
                                if (locationOptions?.length > 0) {
                                    setShowPopup(true);
                                }
                            }}
                        />
                        <InputRightElement height={'32px'}>
                            {isFetching ? (
                                <Spinner boxSize={3} />
                            ) : (
                                searchTerm?.length > 0 && (
                                    <CloseIcon boxSize={3} color='gray.500' onClick={() => setSearchTerm('')} />
                                )
                            )}
                        </InputRightElement>
                    </InputGroup>

                    {isLoading ? (
                        <Flex align={'center'} justify={'center'} minH={'200px'} minW={'200px'}>
                            <Spinner />
                        </Flex>
                    ) : (
                        showPopup && locationOptions.length > 0 && (
                            <List
                                ref={popupRef}
                                position="absolute"
                                top="100%"
                                width="100%"
                                border="1px solid"
                                borderColor={borderColor}
                                backgroundColor={inputBackground}
                                zIndex="1000"
                                maxHeight="200px"
                                overflowY="auto"
                            >
                                {locationOptions.map((location) => (
                                    <ListItem
                                        key={location.region_id + location.name}
                                        p={'5px'}
                                        px={'10px'}
                                        cursor="pointer"
                                        _hover={{ backgroundColor: hoverColor }}
                                        onClick={() => handleLocationSelect(location)}
                                    >
                                        <Flex justify={'space-between'}>
                                            <Text fontSize={'14px'}>{location.name}</Text>
                                            <Text fontSize={'12px'}>{location.type}</Text>
                                        </Flex>
                                    </ListItem>
                                ))}
                            </List>
                        )
                    )}
                </Box>
            </Flex>
        </Box>
    );
};

export default LocationSelectorForShopping;
